<template>
  <div ref="paneRoot" class="flex flex-col h-[calc(100vh-52px)] relative min-h-0 overflow-y-auto">
    <div
      v-if="isSidepaneLoading"
      class="absolute inset-0 z-50 flex items-center justify-center bg-surface bg-opacity-75 rounded-lg"
    >
      <div class="flex flex-col items-center gap-3">
        <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-primary"></div>
        <span class="font-medium text-ink-soft">Updating order...</span>
      </div>
    </div>
    <!--
      AB#21279 — the close button that floated here now lives on the tab strip
      above this pane, beside Expand, and closes the current tab.
    -->

    <!--
      The order header as a title block (AB#21302).

      Four rows of bold-label soup — "ORDER# 27919 KARIN PLATTEN", "Reference:
      … PO Num: … CSM: …", "Billing: … Invoices: …" — became three rows with
      one job each: the EYEBROW says what this order is (merchant, and the
      type chips when it is a reorder or remake, with the original admin at
      the right); the TITLE is the order and the one action on its identity,
      with the admin selects right-aligned; the META line carries the facts
      (customer · created · CSM · SR · PO · billing and its invoices), and the
      reference gets its own labelled line because it is the value people
      copy and it can be long. Same data, same permissions, same components.

      It degrades in a fixed order rather than wrapping wherever it breaks:
      the title never wraps; below md the admins drop to their own row and
      the original-admin note goes under the eyebrow; the meta line wraps by
      item, so a new line always starts with a label.
    -->
    <div ref="headerContainer" class="static md:sticky top-0 z-[31] bg-app">
      <div class="ohd">
        <!-- Eyebrow: what this order is -->
        <div class="ohd-eyebrow">
          <BuildingStorefrontIcon class="ohd-eyebrow-icon" aria-hidden="true" />
          <span class="ohd-merchant" :title="selectedOrderDetails?.company_name">{{ selectedOrderDetails?.company_name || '—' }}</span>
          <!--
            AB#20578 — which order this remake/reorder replaces, and who owned
            it. The original admin's three states are stated rather than
            collapsed into a blank: "never assigned" and "no longer active"
            need different follow-up.
          -->
          <span v-if="originalOrderInfo" class="ohd-chip ohd-chip--amber" data-test="ohd-original">
            {{ originalOrderInfo.label }} of #{{ originalOrderInfo.orderNo }}
          </span>
          <!-- The other direction: standing on the original, what replaced it. -->
          <span
            v-for="derived in derivedOrdersInfo"
            :key="derived.orderNo"
            class="ohd-chip ohd-chip--sky"
            data-test="ohd-derived"
          >
            Replaced by {{ derived.label }} #{{ derived.orderNo }}
          </span>
          <span v-if="originalOrderInfo" class="ohd-eyebrow-r" data-test="ohd-original-admin">
            Original order admin:
            <b v-if="originalOrderInfo.adminName">{{ originalOrderInfo.adminName }}</b>
            <b v-else-if="originalOrderInfo.adminHasLeft" class="ohd-warn">no longer active</b>
            <b v-else class="ohd-warn">never assigned</b>
          </span>
        </div>

        <!-- Title: the order, and who owns it -->
        <div class="ohd-title-row">
          <div class="ohd-title">
            <h2 v-if="headerOrderNumberLabel" class="ohd-h" data-test="ohd-title">Order #{{ headerOrderNumberLabel }}</h2>
            <h2 v-else class="ohd-h" data-test="ohd-title">Order pending confirmation</h2>
            <!--
              AB#20569 / AB#20571 — changing the type as its own action. can()
              only decides whether the button is offered; the route and the
              service still refuse a role that may not.
            -->
            <button
              v-if="can('change-order-type')"
              type="button"
              class="ohd-chip-ghost"
              @click="showChangeOrderType = true"
            >
              Change order type
            </button>
          </div>
          <div
            v-if="can('assign-order-administrator') && selectedRow?.factory_id"
            class="ohd-admins"
          >
            <div ref="selectContainer" class="ohd-admin">
              <span class="ohd-admin-lbl">Order admin</span>
              <Select
                :options="
                  orderAdmins.filter((admin: OrderAdminUser) => {
                    // A Lead Order Administrator is offered for every factory.
                    const formattedRoleNames = admin.role_names?.map((role) =>
                      role.replace(/\s+/g, '')
                    );
                    if (
                      formattedRoleNames?.some(
                        (role) => role.toLowerCase() === 'leadorderadministrator'
                      )
                    ) {
                      return true;
                    }
                    return admin.factory_ids?.includes((selectedRow as DashboardItem).factory_id);
                  })
                "
                :modelValue="selectedRow.order.order_administrator"
                @update:modelValue="onAssignAdmin"
                track-by="id"
                label="name"
                classProps="assign-admins-button ohd-select text-sm"
                placeholder="Unassigned"
                append-to-body
              />
            </div>
            <div class="ohd-admin">
              <span class="ohd-admin-lbl">Secondary</span>
              <Select
                multiple
                append-to-body
                :options="filteredOrderAdmins"
                :modelValue="selectedSecondaryAdmins"
                @update:modelValue="onAssignSecondaryAdmins"
                placeholder="Add"
                classProps="assign-admins-button ohd-select ohd-select--multi text-sm"
                track-by="id"
                label="name"
              >
                <template #option="{ name }">
                  <p style="margin: 0">{{ name }}</p>
                </template>
              </Select>
            </div>
          </div>
        </div>

        <!-- Meta: the facts -->
        <div class="ohd-meta" data-test="ohd-meta">
          <span class="ohd-meta-item"><b class="capitalize">{{ cleanName(selectedOrderDetails?.customer_name) ? selectedOrderDetails?.customer_name : 'N/A' }}</b></span>
          <span v-if="customerEmail" class="ohd-meta-item" data-test="ohd-email">
            <a :href="`mailto:${customerEmail}`" class="ohd-link">{{ customerEmail }}</a>
          </span>
          <span v-if="createdOn" class="ohd-meta-item">Created {{ createdOn }}</span>
          <span v-if="can('display-csManager') && selectedOrderDetails?.cs_manager" class="ohd-meta-item">CSM <b>{{ selectedOrderDetails.cs_manager?.name }}</b></span>
          <span v-if="can('display-salesRepresentative') && selectedOrderDetails?.sales_representative" class="ohd-meta-item">SR <b>{{ selectedOrderDetails.sales_representative?.name }}</b></span>
          <span class="ohd-meta-item">
            PO <b>{{ factoryPoNumber }}</b>
            <a
              v-if="poFileInfo"
              :href="poFileDownloadUrl"
              :download="poFileName"
              class="ohd-link"
              :title="`Download PO: ${poFileName}`"
            >
              <ArrowTopRightOnSquareIcon class="ohd-link-icon" aria-hidden="true" />Open file
            </a>
          </span>
          <span v-if="orderCompositeInvoiceLabel || orderStripeInvoicesForHeader.length" class="ohd-meta-item ohd-billing" data-test="ohd-billing">
            Billing
            <span v-if="orderCompositeInvoiceLabel" :class="orderCompositeInvoicePillClass">{{ orderCompositeInvoiceLabel }}</span>
            <!--
              Every invoice, each a link to its PDF with its status after it.
              More than three are folded behind "+N more" so a split order
              cannot grow the header a line per invoice.
            -->
            <template v-for="inv in visibleInvoices" :key="String(inv.stripe_invoice_id ?? inv.id)">
              <a
                v-if="stripeInvoicePdfUrl(inv)"
                :href="stripeInvoicePdfUrl(inv) || ''"
                target="_blank"
                rel="noopener noreferrer"
                :download="stripeInvoicePdfDownloadName(inv)"
                class="ohd-inv"
                :title="'Download PDF' + (inv.stripe_invoice_number ? ` (${inv.stripe_invoice_number})` : '')"
              >{{ inv.stripe_invoice_number || inv.stripe_invoice_id }}<i v-if="inv.status">{{ inv.status }}</i></a>
              <span v-else class="ohd-inv ohd-inv--plain" title="PDF not stored yet — refresh after sync">{{ inv.stripe_invoice_number || inv.stripe_invoice_id }}<i v-if="inv.status">{{ inv.status }}</i></span>
            </template>
            <button
              v-if="hiddenInvoiceCount > 0"
              type="button"
              class="ohd-more"
              data-test="ohd-invoices-more"
              @click="showAllInvoices = true"
            >
              +{{ hiddenInvoiceCount }} more
            </button>
          </span>
        </div>

        <!-- Reference: the value people copy -->
        <div v-if="!isNullOrEmpty(customerReferenceNo)" class="ohd-ref" data-test="ohd-reference">
          <span class="ohd-ref-lbl">Reference</span>
          <span class="ohd-ref-val">{{ customerReferenceNo }}</span>
        </div>

        <!-- Style: the garment style from the order item's factory_products,
             lifted into the header (AB#21585) so the Detail tab need not repeat
             it. Sourced from style_name, not order_line (AB#21596). -->
        <div v-if="!isNullOrEmpty(styleLine)" class="ohd-ref" data-test="ohd-style">
          <span class="ohd-ref-lbl">Style</span>
          <span class="ohd-ref-val">{{ styleLine }}</span>
        </div>
      </div>
    </div>
    <!--
      The sections are icons at the right end of the action row now
      (OrderSectionNav), not a centred row of pills: the pills cost a full
      band of the pane's height for four words. Tabs still owns which one
      is active; the nav is rendered through its actions slot.
    -->
    <Tabs
      v-if="selectedOrderDetails"
      :tabs="filteredOrderDetailTabs"
      :initial-tab="initialTab"
      :key="selectedRow?.id"
      :show-button="false"
      @change="(tabId: string) => emits('detailsTabChange', tabId)"
      sticky
      sticky-class="static md:sticky z-30 bg-app overflow-hidden dynamic-sticky-tabs"
    >
      <template #actions="{ activeTab, setActiveTab }">
        <div class="flex justify-center items-center px-4 border-b mt-0 overflow-y-auto">
          <OrderDetailActions
            ref="orderActions"
            :current-tab="currentTab"
            :order_item_list="selectedRow"
            :order_item_price="selectedRow.order?.price_info?.order_final_price"
            :order="selectedOrderDetails"
            @onFetch="() => emits('fetchOnSave', true)"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
          >
            <template #nav>
              <OrderSectionNav :tabs="filteredOrderDetailTabs" :active-tab="activeTab" @select="setActiveTab">
                <!--
                  The freshness dot (AB#21202): amber once the stored reading
                  has events behind it; absent until the tab has been opened
                  at least once for this order.
                -->
                <template #elsa-suffix>
                  <span
                    v-if="elsaReadIsStale"
                    class="tabs-tab-dot elsa-stale-dot osec-elsa-dot"
                    data-test="elsa-stale-dot"
                    title="ELSA's read is older than this order's latest event"
                  ></span>
                </template>
              </OrderSectionNav>
            </template>
          </OrderDetailActions>
        </div>
      </template>
      <template #timeline>
        <div class="p-5 overflow-y-auto mt-5">
          <OrderTimeline
            :order="selectedOrderDetails"
            :order_item="currentOrderItem"
            :order_item_index="currentOrderItemIndex"
            :order_no="selectedOrderDetails.order_no"
            :isLoading="isSidepaneLoading"
            :current-tab="currentTab"
            :selectedRow="selectedRow"
            @onFetch="() => emits('fetchOnSave', true)"
            @orderMoved="emits('handleOrderMoved')"
            @orderUpdated="emits('handleOrderUpdated')"
            @dirtyChange="(dirty: boolean) => emits('dirtyChange', dirty)"
          />
        </div>
      </template>
      <template #detail>
        <div class="p-5 overflow-y-auto mt-0">
          <FactoryProductsTab
            v-if="currentOrderItem?.factory_products?.length"
            :factoryProducts="JSON.parse(JSON.stringify(currentOrderItem?.factory_products))"
            :order="selectedOrderDetails"
            :order_item_id="currentOrderItem?.id"
            :order-item="currentOrderItem"
            :order_item_price="selectedRow.price_info?.order_item_final_price"
            :order_line="currentOrderItem?.order_line ?? null"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            :key="JSON.stringify(currentOrderItem?.factory_products)"
            :copiedRoster="copiedRoster"
            @onCopyRoster="copyRoster"
          />
          <p class="text-ink-soft" v-else>No factory products available.</p>
        </div>
      </template>
      <template #files>
        <div class="p-5 overflow-y-auto mt-0">
          <div v-if="currentOrderItem?.factory_id" class="p-5 border border-line rounded-lg">
            <h2 class="text-xl font-semibold mb-3">Files Content</h2>
            <OrderFiles
              :order="currentOrderItem"
              :order-id="currentOrderItem.order_id"
              :order-factories="[
                {
                  id: currentOrderItem.factory_id,
                  name: currentOrderItem.factory_name,
                },
              ]"
              @show-logs="showFileLogs"
              @loading-logs="toggleFileLogsLoading"
            />
          </div>
          <div
            v-else
            class="p-5 border border-line rounded-lg flex items-center w-full justify-center bg-surface"
          >
            <EmptyState title="Oops!" message="Please assign a factory to view files.." />
          </div>
          <SharePointActivityLogs
            :showActivitiesPanel="sharepointLogsVisible"
            :isSharepointLogsLoading="isSharepointLogsLoading"
            :sharePointActivityLogsObject="sharepointActivityLogsObject"
            @close-sharepoint-activity-panel="closeSharepointActivityLog"
          >
          </SharePointActivityLogs>
        </div>
      </template>
      <template #activity_logs>
        <div class="p-5 overflow-y-auto mt-10 md:mt-0 flex flex-col min-h-0 flex-1">
          <OrderActivityLogsTab
            :order-id="selectedOrderDetails.id"
            :order-item-id="selectedRow?.id"
            :row-key="selectedRow?.id"
          />
        </div>
      </template>
      <!--
        AB#21202 — Ask ELSA. Keyed on the order item so switching orders remounts
        it: an analysis is about one specific order, and the one thing this tab
        must never do is show the previous order's reading under the current
        order's number.
      -->
      <template #ask_elsa>
        <div class="overflow-y-auto mt-10 md:mt-0 flex flex-col min-h-0 flex-1">
          <AskElsaTab
            v-if="selectedRow?.id"
            :key="selectedRow.id"
            :order-item-id="selectedRow.id"
            :order-no="selectedOrderDetails?.order_no ?? ''"
            @staleness="(stale: boolean) => (elsaReadIsStale = stale)"
            @act="onElsaAction"
          />
        </div>
      </template>
    </Tabs>
  </div>

  <ChangeOrderTypeModal
    :show="showChangeOrderType"
    :order-no="selectedRow?.order?.order_no ?? selectedRow?.order?.id ?? ''"
    :current-symbol="selectedRow?.order?.order_type_symbol ?? null"
    @close="showChangeOrderType = false"
    @saved="emitOrderTypeChanged"
  />
</template>
<script setup lang="ts">
import FactoryProductsTab from '@/components/Dashboard/Order/components/FactoryProductsTab.vue';
import OrderTimeline from '@/components/Dashboard/Order/OrderTimeline.vue';
import EmptyState from '@/components/Shared/EmptyState.vue';
import Select from '@/components/Shared/Select.vue';
import ChangeOrderTypeModal from '@/components/Dashboard/Modals/ChangeOrderTypeModal.vue';
import Tabs from '@/components/Shared/Tabs.vue';
import { can, cleanName, isNullOrEmpty } from '@/composables';
import { ArrowTopRightOnSquareIcon, BuildingStorefrontIcon } from '@heroicons/vue/24/outline';
import { DashboardItem, OrderAdminUser, OrderDetail, OrderDetailItem } from '@/models';
import { CopiedRoster } from '@/models/CopiedRoster';
import { SharepointActivityLogsObject } from '@/models/SharepointActivityLog';
import { useMainStore } from '@/store';
import { invoiceCompositeStatusPillClass } from '@/utils/invoiceCompositeStatusDisplay';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AskElsaTab from './components/AskElsaTab.vue';
import OrderSectionNav from './components/OrderSectionNav.vue';
import OrderActivityLogsTab from './components/OrderActivityLogsTab.vue';
import OrderDetailActions from './components/OrderDetailActions.vue';
import SharePointActivityLogs from './components/SharePointActivityLogs.vue';
import OrderFiles from './OrderFiles.vue';

interface Tab {
  id: string;
  label: string;
}
const { updateOrderAdmin } = useMainStore();
interface Props {
  currentTab?: string;
  selectedOrderDetails: OrderDetail;
  isSidepaneLoading: boolean;
  selectedRow: DashboardItem;
  filteredOrderDetailTabs: Tab[];
  currentOrderItem: OrderDetailItem | null;
  currentOrderItemIndex: number | null;
  sidebarOpen: boolean;
  initialTab: string;
  orderAdmins: OrderAdminUser[];
}

const props = defineProps<Props>();
const emits = defineEmits([
  'fetchOnSave',
  'handleOrderMoved',
  'handleOrderUpdated',
  'openOrderPriceModal',
  'detailsTabChange',
  'onClose',
  // AB#21279 — the move-status form has unsaved input (or no longer does).
  'dirtyChange',
]);
/**
 * After a type change the order's type, its original-order link and its timeline have all
 * moved, so the list has to be refetched rather than patched locally — the change also
 * writes an activity log entry and a comment, neither of which this component owns.
 *
 * Uses the existing `fetchOnSave` channel that DashboardActions already listens to, instead
 * of reaching into the store: getDashboardData() needs the current DashboardRequest, and
 * this component does not have it.
 */
function emitOrderTypeChanged(): void {
  emits('fetchOnSave', true);
}

const selectContainer = ref<HTMLElement | null>(null);
const headerContainer = ref<HTMLElement | null>(null);

/*
 * Ask ELSA's two wires into the rest of the page (AB#21202).
 *
 * `elsaReadIsStale` drives the dot on the tab. It is reported upward by the tab
 * rather than fetched here on purpose: the analysis endpoint is a real query,
 * and firing it for every order anybody opens — whether or not they ever look
 * at the tab — is a cost the dot does not justify. The consequence is that the
 * dot is absent until the tab has been opened once for this order, which is the
 * honest state: nobody has asked.
 *
 * `onElsaAction` lets the read card's "do this next" buttons open the same
 * modals the action row opens, instead of telling a CSM to go and find the
 * button themselves. Reset when the selected order changes, or a stale dot
 * would follow the reader onto the next order.
 */
const elsaReadIsStale = ref(false);
const orderActions = ref<InstanceType<typeof OrderDetailActions> | null>(null);

const onElsaAction = (action: 'internal-comment' | 'support-threads', text?: string): void => {
  if (action === 'internal-comment') {
    // AB#21422 — a draft from Talk to ELSA opens the note box with the text in
    // it; the person still reads it and presses Save.
    orderActions.value?.openInternalNote?.(text);

    return;
  }

  orderActions.value?.openSupportThreads?.();
};

watch(
  () => props.selectedRow?.id,
  () => {
    elsaReadIsStale.value = false;
  }
);

// Computed properties for header fields
/** First order item’s `item_no` when present; otherwise `order_no`. */
const headerOrderNumberLabel = computed(() => {
  const items = props.selectedOrderDetails?.items;
  const itemNo = items?.[0]?.item_no;
  if (itemNo != null && String(itemNo).trim() !== '') {
    return String(itemNo).trim();
  }
  const orderNo = props.selectedOrderDetails?.order_no;
  if (orderNo != null && String(orderNo).trim() !== '') {
    return String(orderNo).trim();
  }
  return '';
});

const customerReferenceNo = computed(() => {
  return props.selectedOrderDetails?.customer_reference_no || '';
});

// AB#21585 — lifted into the header from the old Order-details card so the
// Detail tab no longer repeats what the header already shows. Email is
// customer-level.
const customerEmail = computed(
  () => (props.selectedOrderDetails as unknown as { customer?: { email?: string } })?.customer?.email || ''
);

// AB#21596 — the Style line shows the garment style, from the order item's
// factory_products (`style_name`, falling back to the product name). It used to
// read `order_line`, which is NOT a style at all but the JSON array of custom
// charge rows (freight, surcharges — see OrderItemOrderLine). Any order carrying
// one of those rows therefore rendered the raw JSON object under a "Style"
// label. Distinct style names are joined; an item with none shows no Style line.
const styleLine = computed(() => {
  const products = props.currentOrderItem?.factory_products ?? [];
  const names = products
    .map((p) => String(p?.style_name ?? p?.new_product_name ?? p?.product_name ?? '').trim())
    .filter((name) => name !== '');
  return [...new Set(names)].join(' · ');
});

const factoryPoNumber = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_number || 'N/A';
});

const poFileInfo = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_file_info;
});

const poFileDownloadUrl = computed(() => {
  if (!poFileInfo.value) return '';
  const BASE_URL = import.meta.env.VITE_APP_FILE_URL;
  return `${BASE_URL}/${poFileInfo.value.path}`;
});

const poFileName = computed(() => {
  if (!poFileInfo.value) return '';
  return poFileInfo.value.name || poFileInfo.value.filename || 'PO_Document';
});

/** Raw composite key for the selected line item (for pill colors). */
const orderItemCompositeStatusRaw = computed(
  () => (props.selectedRow as DashboardItem | undefined)?.invoice_composite_status
);

const orderCompositeInvoicePillClass = computed(() =>
  invoiceCompositeStatusPillClass(
    orderItemCompositeStatusRaw.value == null ||
      String(orderItemCompositeStatusRaw.value).trim() === ''
      ? null
      : String(orderItemCompositeStatusRaw.value)
  )
);

/** Composite billing status for the selected line item (`order_items.invoice_composite_status`). */
const orderCompositeInvoiceLabel = computed(() => {
  const row = props.selectedRow as DashboardItem | undefined;
  const s = row?.invoice_composite_status;
  return s != null && String(s).trim() !== '' ? String(s).replace(/_/g, ' ') : '';
});

/** The order's creation date for the meta line, short. */
const createdOn = computed(() => {
  const raw = props.selectedOrderDetails?.created_at;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  // "14 Sep 2026" — the dashboard's short-date form, with the full year.
  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;
});

/** How many invoices the meta line shows before folding the rest behind "+N more". */
const INVOICES_SHOWN = 3;
const showAllInvoices = ref(false);
const visibleInvoices = computed(() =>
  showAllInvoices.value ? orderStripeInvoicesForHeader.value : orderStripeInvoicesForHeader.value.slice(0, INVOICES_SHOWN)
);
const hiddenInvoiceCount = computed(() => orderStripeInvoicesForHeader.value.length - visibleInvoices.value.length);

/** Deduped Stripe invoices from all line items (`item_invoices`). */
const orderStripeInvoicesForHeader = computed((): Record<string, unknown>[] => {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  const items = props.selectedOrderDetails?.items ?? [];
  for (const it of items) {
    const row = it as unknown as Record<string, unknown>;
    const raw = row.item_invoices ?? row.itemInvoices;
    if (!Array.isArray(raw)) continue;
    for (const inv of raw) {
      const rec = inv as Record<string, unknown>;
      const sid = rec.stripe_invoice_id;
      if (sid == null || sid === '') continue;
      const key = String(sid);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rec);
    }
  }
  return out;
});

/** Stripe-hosted PDF URL on `order_item_invoices.invoice_pdf` — opens/downloads PDF, not the payment page. */
function stripeInvoicePdfUrl(inv: Record<string, unknown>): string | null {
  const pdf = inv?.invoice_pdf;
  if (typeof pdf === 'string' && pdf.trim() !== '') {
    return pdf.trim();
  }
  return null;
}

function stripeInvoicePdfDownloadName(inv: Record<string, unknown>): string {
  const num = inv?.stripe_invoice_number;
  const base =
    typeof num === 'string' && num.trim() !== ''
      ? num.trim().replace(/[^\w.-]+/g, '_')
      : String(inv?.stripe_invoice_id ?? 'invoice');
  return `${base}.pdf`;
}

const showChangeOrderType = ref(false);

/**
 * AB#20578 — the original order behind a remake, and its Order Administrator.
 *
 * Returns null when there is no link, which is the normal case: only remakes and reorders
 * have one, and orders created before AB#20572 have none until the backfill runs
 * (php artisan orders:backfill-original-order-id).
 *
 * adminHasLeft distinguishes the two ways the administrator can be missing. The API sends
 * original_order.order_administrator_id alongside the relation precisely so this is
 * answerable: an id with no object means the user is soft-deleted, i.e. they have left.
 */
const originalOrderInfo = computed<{
  orderNo: string | number;
  label: string;
  adminName: string | null;
  adminHasLeft: boolean;
} | null>(() => {
  const order = props.selectedRow?.order as Record<string, any> | undefined;
  const original = order?.original_order;
  if (!original) return null;

  const admin = original.order_administrator ?? null;

  return {
    orderNo: original.order_no ?? original.id,
    // Read off THIS order's type, not a hardcoded "Remake": a reorder carries the same link
    // and calling it a remake on screen would misreport the remake rate to anyone reading it.
    label: linkLabel(order?.order_type_symbol),
    adminName: admin?.name ?? null,
    adminHasLeft: !admin && Boolean(original.order_administrator_id),
  };
});

/** 'Ri'/'R' are remakes, 'REORDER' a reorder; anything else only that it replaces something. */
function linkLabel(symbol?: string | null): string {
  if (symbol === 'Ri' || symbol === 'R') return 'Remake';
  if (symbol === 'REORDER') return 'Reorder';
  return 'Replacement';
}

/** The remakes and reorders made FROM this order, newest information first on screen. */
const derivedOrdersInfo = computed<{ orderNo: string | number; label: string }[]>(() => {
  const derived = (props.selectedRow?.order as Record<string, any> | undefined)?.derived_orders;
  if (!Array.isArray(derived)) return [];

  return derived.map((d: Record<string, any>) => ({
    orderNo: d.order_no ?? d.id,
    label: linkLabel(d.order_type_symbol),
  }));
});

const filteredOrderAdmins = computed(() => {
  return props.orderAdmins.filter((admin: OrderAdminUser) => {
    // If admin has 'leadorderAdministrator' role, include them without factory_id filtering
    // Format role names by removing spaces for comparison
    const formattedRoleNames = admin.role_names?.map((role) => role.replace(/\s+/g, ''));
    if (formattedRoleNames?.some((role) => role.toLowerCase() === 'leadorderadministrator')) {
      return true;
    }
    // Otherwise, filter by factory_id
    return admin.factory_ids?.includes((props.selectedRow as DashboardItem).factory_id);
  });
});

const selectedSecondaryAdmins = computed(() => {
  return (
    props.selectedRow?.order?.secondary_order_administrators
      ?.map((item) => item.user)
      .filter(Boolean) || []
  );
});
const copiedRoster = ref<CopiedRoster>({
  isAnyRosterCopied: false,
  copiedFromProductIndex: null,
  rosterData: [],
});
const sharepointActivityLogsObject = ref<SharepointActivityLogsObject>(null);
const sharepointLogsVisible = ref<Boolean>(false);
const isSharepointLogsLoading = ref(false);

const onAssignAdmin = async (newValue: OrderAdminUser) => {
  const secondaryAdminIds = [];
  const secondaryAdmin =
    props.selectedRow?.order?.secondary_order_administrators
      ?.map((item) => item.user)
      .filter(Boolean) || [];
  if (Array.isArray(secondaryAdmin) && secondaryAdmin.length) {
    secondaryAdminIds.push(...secondaryAdmin.map((u) => u.id));
  }

  const payload = {
    user_id: newValue ? newValue.id : null,
    order_ids: props.selectedRow.order.id,
    secondary_administrator_ids: secondaryAdminIds,
  };
  await updateOrderAdmin(payload);
  emits('fetchOnSave', true);
};

const onAssignSecondaryAdmins = async (newValues: OrderAdminUser[]) => {
  const secondaryAdmin = newValues;
  // selectedSecondaryOrderAdmin.value = newValues;

  const secondaryAdminIds = secondaryAdmin.map((user) => user.id);

  const payload = {
    user_id: props.selectedRow.order.order_administrator
      ? props.selectedRow.order.order_administrator.id
      : null,
    order_ids: props.selectedRow.order.id,
    secondary_administrator_ids: secondaryAdminIds,
  };
  await updateOrderAdmin(payload);
  emits('fetchOnSave', true);
};

const copyRoster = (data) => {
  copiedRoster.value.isAnyRosterCopied = data.copiedRoster.isAnyRosterCopied;
  copiedRoster.value.copiedFromProductIndex = data.copiedRoster.copiedFromProductIndex;
  copiedRoster.value.rosterData = data.copiedRoster.rosterData;
};
const showFileLogs = async (fileLogsResult: SharepointActivityLogsObject) => {
  sharepointActivityLogsObject.value = fileLogsResult;
};

const toggleFileLogsLoading = () => {
  sharepointLogsVisible.value = true;
  isSharepointLogsLoading.value = !isSharepointLogsLoading.value;
};

const closeSharepointActivityLog = () => {
  sharepointLogsVisible.value = false;
};

const getFileName = (action) => {
  const key = Object.keys(action)[0];
  const obj = action[key];
  // pick correct filename field
  return obj.oldName || obj.name || 'N/A';
};
const formatDate = (date) => {
  return new Date(date).toLocaleString();
};
const getActionType = (action) => {
  // returns the key (rename, delete, etc.)
  return Object.keys(action)[0];
};
/*
 * The action row is sticky, offset by the header's height through a CSS
 * variable. That variable used to be set on the DOCUMENT, and only measured
 * on mount, window resize and factory change. With five orders open in tabs
 * there are five panes writing the same global — and their headers differ in
 * height (a reorder has the type chips, some have invoices, in the split view
 * lines wrap) — so whichever measured last won: the sticky row floated below
 * the real header, leaving a white band, while covering the top of the
 * content. Now each pane sets the variable on its own root, and a
 * ResizeObserver keeps it right whenever the header changes shape, for any
 * reason.
 */
let headerObserver: ResizeObserver | null = null;
onMounted(async () => {
  await nextTick();
  updateStickyOffset();
  window.addEventListener('resize', updateStickyOffset);
  if (typeof ResizeObserver !== 'undefined' && headerContainer.value) {
    headerObserver = new ResizeObserver(() => updateStickyOffset());
    headerObserver.observe(headerContainer.value);
  }
});
watch(
  () => props.selectedRow?.factory_id,
  async (factoryId) => {
    if (!factoryId) return;

    await nextTick();
    updateStickyOffset();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStickyOffset);
  headerObserver?.disconnect();
  headerObserver = null;
});

const paneRoot = ref<HTMLElement | null>(null);
const updateStickyOffset = () => {
  const header = headerContainer.value;
  const root = paneRoot.value;
  if (!header || !root) return;
  // On this pane, not the document: five open orders are five panes with
  // five different header heights.
  root.style.setProperty('--dynamic-header-height', `${header.offsetHeight}px`);
};
</script>
<style>
.dynamic-sticky-tabs {
  top: var(--dynamic-header-height);
}

/*
 * The header as a title block (AB#21302). px sizes: the root font is 12.8px.
 * Not scoped: the Select renders through its own component.
 */
.ohd {
  @apply px-4 pt-[12px] pb-[10px] border-b border-line flex flex-col gap-[6px];
}
.ohd-eyebrow {
  @apply flex flex-wrap items-center gap-x-[8px] gap-y-[4px] text-[12px] text-ink-soft;
}
.ohd-eyebrow-icon {
  @apply w-[15px] h-[15px] shrink-0 text-ink-mute;
}
.ohd-merchant {
  @apply font-medium text-ink-soft truncate max-w-[320px];
}
.ohd-chip {
  @apply rounded-[4px] px-[7px] py-[1px] text-[11px] font-semibold whitespace-nowrap;
}
.ohd-chip--amber {
  @apply bg-[var(--warning-soft)] text-[var(--warning)];
}
.ohd-chip--sky {
  @apply bg-primary-light text-primary;
}
.ohd-eyebrow-r {
  @apply ml-auto text-[11.5px] text-ink-mute;
}
.ohd-eyebrow-r b {
  @apply font-medium text-ink-soft;
}
.ohd-warn {
  @apply text-[var(--danger)] !important;
}
.ohd-title-row {
  @apply flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[8px];
}
.ohd-title {
  @apply flex items-center gap-[12px] min-w-0;
}
.ohd-h {
  @apply m-0 text-[20px] leading-[26px] font-bold text-ink whitespace-nowrap tracking-[-0.01em];
}
.ohd-chip-ghost {
  @apply whitespace-nowrap rounded-full border border-[var(--accent)] bg-surface px-[10px] py-[3px] text-[11px] font-medium
         text-[var(--accent)] transition-colors hover:bg-[var(--accent)] hover:text-white;
}
.ohd-admins {
  @apply flex gap-[8px] min-w-0;
}
.ohd-admin {
  @apply flex items-center gap-[6px] min-w-0;
}
.ohd-admin-lbl {
  @apply text-[11px] text-ink-mute whitespace-nowrap;
}
.ohd-select {
  min-width: 150px;
  max-width: 260px;
}
.ohd-select--multi {
  min-width: 170px;
  max-width: 320px;
}
.ohd-meta {
  @apply flex flex-wrap items-center gap-x-[8px] gap-y-[4px] text-[12.5px] text-ink-soft;
}
.ohd-meta-item {
  @apply inline-flex items-center gap-[5px] whitespace-nowrap;
}
.ohd-meta-item + .ohd-meta-item::before {
  content: '·';
  @apply text-ink-mute mr-[3px];
}
.ohd-meta b {
  @apply font-semibold text-ink;
}
.ohd-billing {
  @apply flex-wrap;
}
.ohd-link {
  @apply inline-flex items-center gap-[3px] text-primary hover:underline text-[12px];
}
.ohd-link-icon {
  @apply w-[12px] h-[12px];
}
.ohd-inv {
  @apply text-[var(--accent)] font-medium hover:underline;
}
.ohd-inv--plain {
  @apply text-ink-soft hover:no-underline;
}
.ohd-inv i {
  @apply not-italic text-ink-mute font-normal text-[11px] ml-[3px];
}
.ohd-more {
  @apply text-[11.5px] text-primary bg-transparent border-0 p-0 cursor-pointer hover:underline;
}
.ohd-ref {
  @apply flex items-baseline gap-[8px] text-[12.5px] min-w-0;
}
.ohd-ref-lbl {
  @apply text-[11px] uppercase tracking-[0.04em] text-ink-mute shrink-0;
}
.ohd-ref-val {
  @apply font-medium text-ink break-words min-w-0;
}
/* Below md: the admins take their own row; the original-admin note goes under the eyebrow. */
@media (max-width: 767px) {
  .ohd-admins {
    @apply basis-full flex-col gap-[6px];
  }
  .ohd-select,
  .ohd-select--multi {
    max-width: none;
    width: 100%;
  }
  .ohd-admin {
    @apply w-full;
  }
  .ohd-eyebrow-r {
    @apply basis-full ml-0;
  }
  .ohd-h {
    @apply text-[18px];
  }
}
@media (max-width: 480px) {
  .ohd-meta {
    @apply flex-col items-start gap-y-[3px];
  }
  .ohd-meta-item + .ohd-meta-item::before {
    content: none;
  }
}
.v-select.vs--multiple .vs__dropdown-toggle .vs__selected-options .vs__selected {
  font-size: 11px !important;
}

.assign-admins-button {
  background: white;
}
</style>
